export const CONSENT_COOKIE = 'jaty-consent'
export const CONSENT_DAYS = 5 * 365
export const CONSENT_VERSION = 1

export function analyticsPath (router, path) {
  try {
    const { route } = router.resolve((path || '/').split(/[?#]/)[0])
    const match = route.matched[route.matched.length - 1]
    if (!match || match.path.includes('*')) return '/other'
    return match.path.replace(/:slug(?:\([^)]*\))?\??/g, ':profile') || '/'
  } catch (_) { return '/other' }
}

export function createConsent (doc, now = () => Date.now()) {
  const state = { analytics: false, decided: false, open: false, persisted: true }
  const listeners = []
  let record = null
  let memoryOnly = false
  const read = name => {
    if (!doc) return ''
    try {
      const pair = doc.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='))
      return pair ? decodeURIComponent(pair.slice(name.length + 1)) : ''
    } catch (_) { return '' }
  }
  const write = (name, value, seconds) => {
    if (!doc) return
    try {
      doc.cookie = name + '=' + encodeURIComponent(value) + '; Path=/; SameSite=Lax' +
        (doc.location.protocol === 'https:' ? '; Secure' : '') +
        (seconds === undefined ? '' : '; Max-Age=' + seconds)
    } catch (_) {}
  }
  const readStored = () => {
    try { return doc && doc.defaultView && doc.defaultView.localStorage.getItem(CONSENT_COOKIE) || '' } catch (_) { return '' }
  }
  const store = json => {
    try {
      const storage = doc && doc.defaultView && doc.defaultView.localStorage
      if (!storage) return false
      storage.setItem(CONSENT_COOKIE, json)
      return storage.getItem(CONSENT_COOKIE) === json
    } catch (_) { return false }
  }
  function remove (name) {
    // Remove old host-only and parent-domain cookies, including path variants.
    const host = doc.location.hostname.split('.')
    const domains = ['']
    while (host.length > 1) {
      domains.push(host.join('.'), '.' + host.join('.'))
      host.shift()
    }
    const paths = ['/']
    const parts = doc.location.pathname.split('/').filter(Boolean)
    while (parts.length) { paths.push('/' + parts.join('/'), '/' + parts.join('/') + '/'); parts.pop() }
    for (const domain of domains) for (const path of paths) {
      try { doc.cookie = name + '=; Max-Age=0; Path=' + path + (domain ? '; Domain=' + domain : '') } catch (_) {}
    }
  }
  function cleanup (includeAnalytics = true) {
    if (!doc) return
    let names = []
    try { names = doc.cookie.split(';').map(c => c.trim().split('=')[0]) } catch (_) {}
    for (const name of names) {
      const retired = /^(?:_gcl_|_hj|_fbp$|_fbc$|__gads$|__gpi$|__eoi$)/.test(name)
      const analytics = /^(?:_ga(?:_|$)|_gid$|_gat|first_page_visited$|first_utm_source$)/.test(name)
      if (retired || (includeAnalytics && analytics)) remove(name)
    }
  }
  function valid (value) {
    return value && value.version === CONSENT_VERSION && typeof value.analytics === 'boolean' &&
      Number.isFinite(value.savedAt) && value.savedAt <= now() &&
      Number.isFinite(value.expiresAt) && value.expiresAt > now() &&
      value.expiresAt > value.savedAt && value.expiresAt - value.savedAt <= CONSENT_DAYS * 86400000
  }
  function apply (value) {
    const changed = state.analytics !== !!(value && value.analytics) || state.decided !== !!value
    record = value
    state.analytics = !!(value && value.analytics)
    state.decided = !!value
    if (!value) state.open = true
    if (changed) listeners.forEach(fn => fn(state.analytics))
    if (!state.analytics) cleanup()
  }
  function sync () {
    if (!doc) return false
    if (memoryOnly && valid(record)) return state.analytics
    let value
    try {
      // Cookies can expire earlier than the choice (Chrome caps them at 400
      // days). Keep the original deadline in localStorage, without sliding it.
      const records = [read(CONSENT_COOKIE), readStored()].filter(Boolean).map(JSON.parse)
      if (records.every(item => item && Number.isFinite(item.savedAt))) {
        records.sort((a, b) => b.savedAt - a.savedAt || Number(a.analytics) - Number(b.analytics))
        value = records[0]
      }
    } catch (_) {}
    if (valid(value)) store(JSON.stringify(value))
    apply(valid(value) ? value : null)
    return state.analytics
  }
  function save (analytics) {
    const savedAt = now()
    const value = { version: CONSENT_VERSION, analytics: analytics === true, savedAt, expiresAt: savedAt + CONSENT_DAYS * 86400000 }
    const json = JSON.stringify(value)
    write(CONSENT_COOKIE, json, CONSENT_DAYS * 86400)
    const stored = store(json)
    memoryOnly = read(CONSENT_COOKIE) !== json && !stored
    state.persisted = !memoryOnly
    apply(value)
    state.open = false
  }
  function attribution () {
    if (!sync()) return { source: '', first_page_visited: '' }
    return { source: read('first_utm_source'), first_page_visited: read('first_page_visited') }
  }
  function capture (route, router) {
    if (!sync()) return
    if (!read('first_page_visited')) write('first_page_visited', analyticsPath(router, route.path))
    const source = route.query && route.query.utm_source
    if (typeof source === 'string' && /^[\p{L}\p{N} _.\-]{1,100}$/u.test(source) && !read('first_utm_source')) {
      write('first_utm_source', source.trim())
    }
  }
  return {
    state, sync, save, attribution, capture, cleanup,
    open () { sync(); state.open = true },
    subscribe (fn) { listeners.push(fn) },
    expiresIn () { return record ? Math.max(0, record.expiresAt - now()) : null }
  }
}
